import React from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { NodeExecutionStatus } from 'types';
import clsxm from '@/lib/clsxm';
import { useWorkflowStore } from '@/lib/workflows/store';

import useLocale from 'next-translate/useTranslation';

import { SimpleTooltip } from '@/components/ui/tooltip';

export type DataType = 'text' | 'image' | 'video' | 'audio';

const CONNECTOR_STYLE_CLASSES: Record<DataType, string> = {
  text: 'bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/50',
  image:
    'bg-purple-50 text-purple-600 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800/50',
  video:
    'bg-cyan-50 text-cyan-600 border border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:border-cyan-800/50',
  audio:
    'bg-indigo-50 text-indigo-600 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/50',
};

// Helper function to create labeled input handle
export function createInputHandle(
  type: DataType,
  id?: string,
  position: number = 50,
  labelOverride?: string,
) {
  const colors = {
    text: { bg: 'blue-500', hover: 'blue-600' },
    image: { bg: 'purple-500', hover: 'purple-600' },
    video: { bg: 'cyan-500', hover: 'cyan-600' },
    audio: { bg: 'indigo-500', hover: 'indigo-600' },
  };

  const labels = {
    text: 'Text Input',
    image: 'Image Input',
    video: 'Video Input',
    audio: 'Audio Input',
  };

  const icons = {
    text: (
      <svg
        className='w-4 h-4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
      </svg>
    ),
    image: (
      <svg
        className='w-4 h-4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
      </svg>
    ),
    video: (
      <svg
        className='w-4 h-4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' />
      </svg>
    ),
    audio: (
      <svg
        className='w-4 h-4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z' />
      </svg>
    ),
  };

  const handleId = id || type;
  const color = colors[type];
  const label =
    labelOverride ||
    (handleId === 'first-frame'
      ? 'First frame'
      : handleId === 'last-frame'
        ? 'Last frame'
        : labels[type]);

  return (
    <SimpleTooltip title={label} side='left'>
      <Handle
        type='target'
        position={Position.Left}
        id={handleId}
        style={{
          top: `calc(${position}%)`,
          left: '-14px',
          zIndex: 1000,
          cursor: 'crosshair',
          width: '24px',
          height: '24px',
          background: 'transparent',
          border: 'none',
        }}
        className='transition-colors'
      >
        <div
          style={{
            width: '24px',
            height: '24px',
          }}
          className={`rounded-full flex items-center justify-center pointer-events-none border-2 ${CONNECTOR_STYLE_CLASSES[type]}`}
          title={label}
        >
          {icons[type]}
        </div>
      </Handle>
    </SimpleTooltip>
  );
}

// Helper function to create labeled output handle
export function createOutputHandle(
  type: DataType,
  id?: string,
  position: number = 50,
) {
  const colors = {
    text: { bg: 'blue-500', hover: 'blue-600' },
    image: { bg: 'purple-500', hover: 'purple-600' },
    video: { bg: 'cyan-500', hover: 'cyan-600' },
    audio: { bg: 'indigo-500', hover: 'indigo-600' },
  };

  const labels = {
    text: 'Text Output',
    image: 'Image Output',
    video: 'Video Output',
    audio: 'Audio Output',
  };

  const icons = {
    text: (
      <svg
        className='w-4 h-4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
      </svg>
    ),
    image: (
      <svg
        className='w-4 h-4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
      </svg>
    ),
    video: (
      <svg
        className='w-4 h-4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' />
      </svg>
    ),
    audio: (
      <svg
        className='w-4 h-4'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z' />
      </svg>
    ),
  };

  const handleId = id || type;
  const color = colors[type];
  const label =
    handleId === 'first-frame'
      ? 'First frame'
      : handleId === 'last-frame'
        ? 'Last frame'
        : labels[type];

  return (
    <SimpleTooltip title={label} side='right'>
      <Handle
        type='source'
        position={Position.Right}
        id={handleId}
        style={{
          top: `calc(${position}%)`,
          right: '-14px',
          cursor: 'crosshair',
          width: '24px',
          height: '24px',
          borderRadius: '9999px',
          background: 'transparent',
          border: 'none',
        }}
        className='transition-colors'
      >
        <div
          style={{
            width: '24px',
            height: '24px',
          }}
          className={`rounded-full flex items-center justify-center pointer-events-none border-2 ${CONNECTOR_STYLE_CLASSES[type]}`}
          title={label}
        >
          {icons[type]}
        </div>
      </Handle>
    </SimpleTooltip>
  );
}

export interface BaseNodeProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  hasInput?: boolean;
  hasOutput?: boolean;
  status?: NodeExecutionStatus;
  selected?: boolean;
  nodeId?: string;
  onRunNode?: (nodeId: string) => void;
  customInputs?: React.ReactNode;
  customOutputs?: React.ReactNode;
  customHeaderButtons?: React.ReactNode;
  disableMinWidth?: boolean;
  readOnly?: boolean;
  minimal?: boolean;
  hideToolbarActions?: boolean;
}

export function BaseNode({
  title,
  icon,
  children,
  hasInput = true,
  hasOutput = true,
  status = 'idle',
  selected,
  nodeId,
  onRunNode,
  customInputs,
  customOutputs,
  customHeaderButtons,
  disableMinWidth,
  readOnly = false,
  minimal = false,
  hideToolbarActions = false,
}: BaseNodeProps) {
  const { t } = useLocale('common');
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const duplicateNode = useWorkflowStore((state) => state.duplicateNode);
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [editedTitle, setEditedTitle] = React.useState(title);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    if (nodeId && confirm(`Delete "${title}" node?`)) {
      deleteNode(nodeId);
    }
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    if (nodeId) {
      duplicateNode(nodeId);
    }
  };

  const handleTitleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    setIsEditingTitle(true);
    setEditedTitle(title);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    setEditedTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    if (readOnly) {
      setIsEditingTitle(false);
      return;
    }
    if (editedTitle.trim() && editedTitle !== title && nodeId) {
      updateNodeData(nodeId, {
        label: editedTitle.trim(),
      });
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setEditedTitle(title);
      setIsEditingTitle(false);
    }
  };

  // Focus input when editing starts
  React.useEffect(() => {
    if (isEditingTitle && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingTitle]);

  // Sync edited title with prop changes
  React.useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  const getStatusStyles = () => {
    switch (status) {
      case 'running':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg shadow-blue-200 dark:shadow-blue-900/50';
      case 'success':
        return 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500';
      case 'error':
        return 'border-red-500 bg-red-50 dark:bg-red-900/20';
      default:
        return 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500';
    }
  };

  const getStatusIndicator = () => {
    switch (status) {
      case 'running':
        return (
          <div className='absolute -top-2 -right-2 w-4 h-4'>
            <div className='w-4 h-4 bg-blue-500 rounded-full animate-ping absolute' />
            <div className='w-4 h-4 bg-blue-600 rounded-full relative' />
          </div>
        );
      case 'success':
        return (
          <div className='absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center'>
            <svg
              className='w-3 h-3 text-white'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path d='M5 13l4 4L19 7' />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className='absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center'>
            <svg
              className='w-3 h-3 text-white'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path d='M6 18L18 6M6 6l12 12' />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const isMinimalView = minimal && !selected && !isHovered;

  return (
    <div
      className='relative'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Title - Positioned above node */}
      {!isMinimalView && (
        <div className='absolute -top-8 left-0 flex items-center gap-2 px-2 py-1 bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 z-10'>
          {icon && <div className='flex-shrink-0'>{icon}</div>}
          {isEditingTitle ? (
            <input
              ref={inputRef}
              type='text'
              value={editedTitle}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className='font-medium text-xs text-gray-900 dark:text-white bg-transparent border-b border-blue-500 dark:border-blue-400 outline-none px-1 min-w-[100px]'
            />
          ) : (
            <div
              className='font-medium text-xs text-gray-900 dark:text-white whitespace-nowrap cursor-text'
              onDoubleClick={handleTitleDoubleClick}
              title={t('Double-click to edit')}
            >
              {title}
            </div>
          )}
        </div>
      )}

      {/* Action Toolbar - Positioned top-right */}
      {(selected || isHovered) && nodeId && !readOnly && (
        <div className='absolute -top-8 right-0 flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 z-10'>
          {onRunNode && status !== 'running' && (
            <SimpleTooltip title={t('Run this node')} side='top'>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRunNode(nodeId);
                }}
                className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
              >
                <svg
                  className='w-5 h-5 text-primary-600 dark:text-primary-400'
                  fill='none'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
                  <path d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
              </button>
            </SimpleTooltip>
          )}
          {customHeaderButtons}
          {!hideToolbarActions && (
            <>
              <SimpleTooltip title={t('Duplicate node')} side='top'>
                <button
                  onClick={handleDuplicate}
                  className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
                >
                  <svg
                    className='w-5 h-5 text-gray-600 dark:text-gray-300'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
                  </svg>
                </button>
              </SimpleTooltip>
              <SimpleTooltip title={t('Delete node')} side='top'>
                <button
                  onClick={handleDelete}
                  className='flex-shrink-0 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors'
                >
                  <svg
                    className='w-4 h-4 text-red-600 dark:text-red-400'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                  </svg>
                </button>
              </SimpleTooltip>
            </>
          )}
        </div>
      )}

      {/* Node Container */}
      <div
        className={clsxm(
          'relative rounded-lg transition-all duration-200',
          !isMinimalView && 'border-2 shadow-sm',
          !disableMinWidth && 'min-w-[280px]',
          isMinimalView
            ? 'border-transparent bg-transparent'
            : getStatusStyles(),
          selected && 'ring-2 ring-primary-500',
        )}
      >
        {customInputs ? (
          customInputs
        ) : hasInput ? (
          <Handle
            type='target'
            position={Position.Left}
            className='!w-3 !h-3 !bg-gray-400 dark:!bg-gray-500 !border-2 !border-white dark:!border-gray-800 hover:!bg-primary-500 dark:hover:!bg-primary-600 transition-colors'
          />
        ) : null}

        {/* Content */}
        <div>{children}</div>

        {/* Status Indicator */}
        {getStatusIndicator()}

        {customOutputs ? (
          customOutputs
        ) : hasOutput ? (
          <Handle
            type='source'
            position={Position.Right}
            className='!w-3 !h-3 !bg-gray-400 dark:!bg-gray-500 !border-2 !border-white dark:!border-gray-800 hover:!bg-primary-500 dark:hover:!bg-primary-600 transition-colors'
          />
        ) : null}
      </div>
    </div>
  );
}
